import { useState, useEffect, useCallback } from 'react';
import type { ProgressoUsuario, Serie } from '@/types/masterclass';

const STORAGE_KEY = 'mc_progress_v1';

export function useMasterClassProgress(serieId: string) {
  const [progress, setProgress] = useState<ProgressoUsuario | null>(null);
  
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const allProgress: ProgressoUsuario[] = JSON.parse(stored);
        const serieProgress = allProgress.find(p => p.serieId === serieId);
        setProgress(serieProgress || null);
      } catch (e) {
        console.error('Erro ao ler progresso:', e);
      }
    }
  }, [serieId]);
  
  const updateProgress = useCallback((
    aulaId: string, 
    timestampAssistido: number, 
    status: 'nao_iniciada' | 'em_andamento' | 'concluida',
    totalAulas: number
  ) => {
    const stored = localStorage.getItem(STORAGE_KEY);
    let allProgress: ProgressoUsuario[] = stored ? JSON.parse(stored) : [];
    
    const index = allProgress.findIndex(p => p.serieId === serieId);
    
    if (index === -1) {
      const newProgress: ProgressoUsuario = {
        userId: 'current',
        serieId,
        aulas: { [aulaId]: { status, timestampAssistido } },
        progressoSeriePct: calculateSerieProgress({ [aulaId]: { status, timestampAssistido } }, totalAulas)
      };
      allProgress.push(newProgress);
    } else {
      allProgress[index].aulas[aulaId] = { status, timestampAssistido };
      allProgress[index].progressoSeriePct = calculateSerieProgress(allProgress[index].aulas, totalAulas);
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allProgress));
    setProgress(allProgress.find(p => p.serieId === serieId) || null);
  }, [serieId]);
  
  return {
    progressoPct: progress?.progressoSeriePct || 0,
    aulas: progress?.aulas || {},
    updateProgress
  };
}

function calculateSerieProgress(
  aulas: Record<string, { status: string; timestampAssistido: number }>, 
  totalAulas: number
): number {
  const aulasCompletas = Object.values(aulas).filter(a => a.status === 'concluida').length;
  return Math.round((aulasCompletas / totalAulas) * 100);
}

export function getAulaProgress(aulaId: string, serieId: string) {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return { status: 'nao_iniciada' as const, timestampAssistido: 0, percent: 0 };
  
  try {
    const allProgress: ProgressoUsuario[] = JSON.parse(stored);
    const serieProgress = allProgress.find(p => p.serieId === serieId);
    const aulaProgress = serieProgress?.aulas[aulaId];
    
    if (!aulaProgress) return { status: 'nao_iniciada' as const, timestampAssistido: 0, percent: 0 };
    
    return {
      status: aulaProgress.status,
      timestampAssistido: aulaProgress.timestampAssistido,
      percent: aulaProgress.status === 'concluida' ? 100 : 0
    };
  } catch (e) {
    console.error('Erro ao ler progresso da aula:', e);
    return { status: 'nao_iniciada' as const, timestampAssistido: 0, percent: 0 };
  }
}
